import React, { useEffect, useState } from 'react';
import { Box, Dialog, TextField } from '@mui/material';
import CustomDialogTitle from '../../customComponents/CustomDialogTitle';
import CustomDialogContent from '../../customComponents/CustomDialogContent';
import { useTheme, useMediaQuery } from '@mui/material';
// import { IOrganization, IOrganizationRequest, IOrganizationResponse } from '../../interfaces/IOrganization';
import { z } from 'zod';
import { AppDispatch } from '../../stateManagement/store';
import { useDispatch } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import { createOrganizationAsync, updateOrganizationAsync } from '../../stateManagement/organizationUserSlice';
import OrganizationService from '../../services/OrganizationService';
import { IOrganization, IOrganizationRequest, IOrganizationResponse } from './OrganizationList';


export const organizationSchema: z.ZodType<IOrganizationRequest> = z.object({
    name: z.string().min(1, "Name cannot be empty.").max(100, "Maximum length of Name is 100 characters."),
    country: z.string().max(50, "Maximum length of country or state is 50 characters.").optional(),
    provinceOrState: z.string().max(50, "Maximum length of province or state is 50 characters.").optional(),
    city: z.string().max(50, "Maximum length of city is 50 characters.").optional(),
    address: z.string().max(200, "Maximum length of Address is 200 characters.").optional(),
    phone: z.string()
        .max(20, "Maximum length of Phone is 20 characters.")
        .regex(/^\d*$/, "Phone number must contain only digits.")
        .optional(),
    website: z.string()
        .max(100, "Maximum length of Website is 100 characters.")
        .regex(/^(https?:\/\/)?(www\.)?[\w\-]+(\.[\w\-]+)+[/#?]?.*$/, "Invalid website format. Example: https://example.com")
        .optional(),
    email: z.string()
        .max(100, "Maximum length of Email is 100 characters.")
        .email("Invalid email format.")
        .optional(),
    defaultTaxPercent: z.number()
        .min(0, "Tax percent must be at least 0%.")
        .max(100, "Tax percent cannot exceed 100%.")
        .optional(),
});

const OrganizationDialog: React.FC<{
    state: { open: boolean; organizationInput?: IOrganizationResponse; };
    closeOrganizationDialog: (input?: IOrganization) => void;
}> = ({ state, closeOrganizationDialog }) => {

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch<AppDispatch>();

    const defaultValues = {
        name: '',
        provinceOrState: '',
        city: '',
        address: '',
        phone: '',
        website: '',
        email: '',
        defaultTaxPercent: 0,
        country: '',
    };

    const { register, handleSubmit, formState: { errors }, reset } = useForm<IOrganizationRequest>({
        resolver: zodResolver(organizationSchema),
        defaultValues: defaultValues
    });

    const handleOrganization = async (data: IOrganizationRequest) => {
        setLoading(true);

        try {
            let result;
            if (state.organizationInput?.organizationId) {
                result = await dispatch(updateOrganizationAsync({ organizationId: state.organizationInput.organizationId, data })).unwrap();
            } else {
                result = await dispatch(createOrganizationAsync(data)).unwrap();
            }
            if (result) {
                closeOrganizationDialog();
            }
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const GetOrganization = async (organizationId: string) => {
        setLoading(true);
        try {

            var organization = await OrganizationService.GetOrganization(organizationId);

            return organization
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }


    useEffect(() => {
        const fetchAndSetOrganization = async () => {
            if (state.organizationInput?.organizationId) {
                const organization = await GetOrganization(state.organizationInput.organizationId);
                if (organization) {
                    reset({
                        name: organization.name ?? '',
                        provinceOrState: organization.provinceOrState ?? '',
                        city: organization.city ?? '',
                        address: organization.address ?? '',
                        phone: organization.phone ?? '',
                        website: organization.website ?? '',
                        email: organization.email ?? '',
                        defaultTaxPercent: organization.defaultTaxPercent ?? 0,
                        country: organization.country ?? '',
                    });
                }
            } else {
                reset(defaultValues);
            }
        };

        fetchAndSetOrganization();
    }, [state.organizationInput, reset]);

    return (
        <Dialog
            open={state.open}
            onClose={() => closeOrganizationDialog()}
            fullWidth
            fullScreen={fullScreen}
        >
            <CustomDialogTitle handleClose={closeOrganizationDialog} title='New Appointment' loading={loading} />
            <CustomDialogContent>
                <form onSubmit={handleSubmit(handleOrganization)} noValidate>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Name"
                            InputLabelProps={{ shrink: true }}
                            {...register('name')}
                            error={!!errors.name}
                            helperText={errors.name?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Country"
                            InputLabelProps={{ shrink: true }}
                            {...register('country')}
                            error={!!errors.country}
                            helperText={errors.country?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Province / State"
                            InputLabelProps={{ shrink: true }}
                            {...register('provinceOrState')}
                            error={!!errors.provinceOrState}
                            helperText={errors.provinceOrState?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="City"
                            InputLabelProps={{ shrink: true }}
                            {...register('city')}
                            error={!!errors.city}
                            helperText={errors.city?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Address"
                            InputLabelProps={{ shrink: true }}
                            {...register('address')}
                            error={!!errors.address}
                            helperText={errors.address?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Phone"
                            InputLabelProps={{ shrink: true }}
                            {...register('phone')}
                            error={!!errors.phone}
                            helperText={errors.phone?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Website"
                            InputLabelProps={{ shrink: true }}
                            {...register('website')}
                            error={!!errors.website}
                            helperText={errors.website?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Email"
                            InputLabelProps={{ shrink: true }}
                            {...register('email')}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            fullWidth
                            type="defaultTaxPercent"
                            label="Default tax percent"
                            InputLabelProps={{ shrink: true }}
                            {...register('defaultTaxPercent', { valueAsNumber: true })}
                            error={!!errors.defaultTaxPercent}
                            helperText={errors.defaultTaxPercent?.message}
                            inputProps={{ step: "0.01", min: 0 }}
                        />
                    </Box>

                    <CustomFormAction>
                        <CustomButton lable='Save' type='submit' disabled={loading} infoContent={`Save the ${state.organizationInput?.organizationId ? 'updated' : 'new'} place record`} />
                    </CustomFormAction>
                </form>
            </CustomDialogContent>
        </Dialog>
    );
};

export default OrganizationDialog;